// src/pages/Reports/components/ReportsTable.tsx
import { useState, useEffect } from "react";
import CustomTable, { TableColumn } from "@components/common/table/CustomTable";
import actionsHandler from "@/store/actions/actionHandler";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import FilterIcon from "@styles/all-images/icons/rejectIcon.svg";
import FilterModal from "@/components/common/FilterModal";
import dateHandler from "@/composables/dateUtil";
import { useNavigate } from "react-router-dom";
import EyeIcon from "@styles/all-images/icons/view-icon.svg";
import EditIcon from "@styles/all-images/icons/edit-icon-blue.svg";
import { UserTierType } from "@/constants/enums/user-tier-type.enum";
import { usePermissionState } from "@/providers/PermissionProvider";
import { ReportsPermissions } from "@/constants/permission-modules/base-permissions";

interface ReportsTableProps {
  userType: UserTierType;
  type: string;
  fetchData: (params: any) => Promise<any>;
  onCompanyChange?: (companyId: number | undefined) => void;
  selectedCompanyId?: number | undefined;
}

interface FilterComponentProps {
  onFilter: (value: string) => void;
}

interface ReportData {
  id: string | number;
  title: string;
  created_at: string;
  created_by: string;
}

const ReportsTable = ({ userType, type, fetchData }: ReportsTableProps) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [reportsData, setReportsData] = useState<any[]>([]);
  const [companies, setCompanies] = useState<any[]>([]);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [forceRefresh, setForceRefresh] = useState(false);
  const [forceReset, setForceReset] = useState(false);
  const [selectedCompanyId, setSelectedCompanyId] = useState<number | null>(
    null,
  );
  const [isLoading, setIsLoading] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { formatDateWithTime } = dateHandler();
  const { isPermitted } = usePermissionState();

  const handleView = (row: ReportData) => {
    navigate(`/reports/view/${row.id}`);
  };

  const handleEdit = (row: ReportData) => {
    navigate(`/reports/edit/${row.id}`);
  };

  useEffect(() => {
    const fetchCompanies = async () => {
      setIsLoading(true);
      try {
        const { authenticatedCall } = actionsHandler("companyList");
        const response = await dispatch(
          authenticatedCall({
            method: "get",
            customQuery: searchTerm ? `search=${searchTerm}` : "",
          }) as any,
        );

        if (response.ok) {
          const transformedCompanies = response.data.map((company: any) => ({
            id: company.id,
            name: company.name,
            email: company.email,
            phone: company.phone,
            address: company.address,
            logo: company.logo,
          }));
          setCompanies(transformedCompanies);
        } else {
          toast.error(response.data?.message || "Failed to fetch companies");
        }
      } catch (error) {
        console.error("Error fetching companies:", error);
        toast.error("Failed to fetch companies");
      } finally {
        setIsLoading(false);
      }
    };

    fetchCompanies();
  }, [dispatch, searchTerm]);

  const handleFetch = async (params: any) => {
    setIsLoading(true);
    try {
      const response = await fetchData({
        ...params,
        page: params.page || currentPage,
        rowsPerPage: params.rowsPerPage || rowsPerPage,
        searchTerm: params.search || searchTerm,
        companyId: selectedCompanyId,
        limit: params.limit || rowsPerPage,
      });

      if (response.ok) {
        return response;
      }
      toast.error(response.data?.message || "Failed to fetch reports");
      return { data: [], total: 0 };
    } catch (error) {
      console.error("Error fetching reports:", error);
      toast.error("Failed to fetch reports");
      return { data: [], total: 0 };
    } finally {
      setIsLoading(false);
    }
  };

  const columns: TableColumn<ReportData>[] = [
    {
      header: "Report Name",
      accessor: (row: unknown) => (row as ReportData).title,
      sortable: true,
      sortKey: "title",
    },
    {
      header: "Created On Date & Time",
      accessor: (row: any) => formatDateWithTime(row.created_at),
      sortable: true,
      sortKey: "created_at",
    },

    {
      header: "Created By",
      accessor: (row: unknown) => (row as ReportData).created_by,
      sortable: true,
      sortKey: "created_by",
    },

    {
      header: "Actions",
      accessor: (row: unknown) => (
        <div className="tw-flex tw-items-center">
          <button onClick={() => handleView(row as ReportData)} title="View">
            <img
              src={EyeIcon}
              alt="View"
              className="tw-w-10 tw-h-10 tw-max-w-10 tw-max-h-10 tw-min-w-10 tw-min-h-10 tw-max-w-6 tw-max-h-6"
            />
          </button>
          {isPermitted(ReportsPermissions.UPDATE_REPORTS) && (
            <button onClick={() => handleEdit(row as ReportData)} title="Edit">
              <img
                src={EditIcon}
                alt="Edit Icon"
                className="tw-w-10 tw-h-10 tw-max-w-10 tw-max-h-10 tw-min-w-10 tw-min-h-10 tw-max-w-5 tw-max-h-5"
              />
            </button>
          )}
        </div>
      ),
    },
  ];
  const handleClosefilterModal = () => {
    setIsFilterModalOpen(false);
  };

  const handleFilter = () => {
    setIsFilterModalOpen(true);
  };

  const handleFilterApply = (newCompanyId: number | null) => {
    setSelectedCompanyId(newCompanyId);
    setIsFilterModalOpen(false);
    setForceRefresh(false);
    setTimeout(() => setForceRefresh(true), 0);
  };

  const getTableButtons = (type: string) => {
    if (userType === UserTierType.Tier3) {
      return {
        filterButton: {
          text: "Filter",
          icon: FilterIcon,
          onClick: () => handleFilter(),
        },
      };
    }
    return {};
  };

  useEffect(() => {
    if (selectedCompanyId) {
      console.log("Selected company id:", selectedCompanyId);
      setForceRefresh(true);
    }
  }, [selectedCompanyId]);

  return (
    <>
      <CustomTable
        columns={columns}
        fetchData={handleFetch}
        forceRefresh={forceRefresh}
        setForceRefresh={setForceRefresh}
        forceReset={forceReset}
        setForceReset={setForceReset}
        filterButton={getTableButtons(type as any).filterButton}
        noDataMessage={`No ${type} reports found`}
        noSearchDataMessage={`No matching ${type} reports found`}
      />
      {isFilterModalOpen && (
        <FilterModal
          onClose={handleClosefilterModal}
          onApply={handleFilterApply}
          isOpen={isFilterModalOpen}
          currentFilter={selectedCompanyId || undefined}
        />
      )}
    </>
  );
};

export default ReportsTable;
